import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useServices } from "../../context/ServiceProvider";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Typography,
  Checkbox,
  Layout,
  Button,
  List,
  Spin,
  Empty,
  Alert,
} from "antd";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useRecents } from "../../hooks/useRecents";
import CreateProjectPageModal from "./CreateProjectPageModal";
import CloneProjectPageModal from "./CloneProjectPageModal";
import { resetBreadcrumbs } from "../../store/slices/navigationSlice";

export default function ProjectPageList() {
  const { projectPage } = useServices();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const dispatch = useDispatch();

  // Dropdown search state
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = React.useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectPage.fetchAllProjectPages();
      setRecords(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [projectPage]);

  // Dropdown search with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const term = (query || "").trim();
    if (term.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await projectPage.searchProjectPages(term);
        setSearchResults(Array.isArray(data) ? data : []);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, projectPage]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    dispatch(resetBreadcrumbs());
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "Name",
        key: "Name",
        width: "8%",
        align: "center",
        ellipsis: true,
        sorter: (a, b) => a.Name.localeCompare(b.Name),
        render: (text, rec) => (
          <Link
            to={{ pathname: `/pp/${rec.Id}` }}
            state={{ crumbLabel: rec.Name }}
            onClick={() => {
              record({
                id: String(rec.Id),
                title: String(text || rec.Name || rec.Id),
                url: `/pp/${rec.Id}`,
                type: "Project Page",
              });
            }}
          >
            {text || rec.Id}
          </Link>
        ),
      },
      {
        title: "Project",
        dataIndex: "Project__r.Name",
        key: "Project",
        width: "10%",
        align: "left",
        ellipsis: true,
        sorter: (a, b) => {
          const projectPageA = a.Project__r;
          const projectPageB = b.Project__r;
          if (!projectPageA || !projectPageB) return -1;
          return projectPageA.Name.localeCompare(projectPageB.Name);
        },
        render: (text, rec) => {
          const project = rec.Project__r;
          if (!project) return null;
          return (
            <Link
              to={{ pathname: `/p/${project.Id}` }}
              state={{ crumbLabel: project.Name }}
              onClick={() =>
                record({
                  id: String(project.Id),
                  title: String(text || project.Name || project.Id),
                  url: `/p/${project.Id}`,
                  type: "Project",
                })
              }
            >
              {text || project.Name}
            </Link>
          );
        },
      },
      {
        title: "Project Objective",
        dataIndex: "Project_Objective__r.Name",
        key: "Project_Objective__r",
        width: "10%",
        align: "left",
        ellipsis: true,
        sorter: (a, b) => {
          const projectPageA = a.Project__r;
          const projectPageB = b.Project__r;
          if (!projectPageA || !projectPageB) return -1;
          return projectPageA.Name.localeCompare(projectPageB.Name);
        },
        render: (text, rec) => {
          const projectObjective = rec.Project_Objective__r;
          if (!projectObjective) return "-";
          return (
            <Link
              to={{ pathname: `/p/${projectObjective.Id}` }}
              state={{ crumbLabel: projectObjective.Name }}
              onClick={() =>
                record({
                  id: String(projectObjective.Id),
                  title: String(
                    text || projectObjective.Name || projectObjective.Id,
                  ),
                  url: `/p/${[projectObjective].Id}`,
                  type: "Project",
                })
              }
            >
              {text || projectObjective.Name}
            </Link>
          );
        },
      },
      {
        title: "Project Qualification Step",
        dataIndex: "Project_Qualification_Step__r.Name",
        key: "Project_Qualification_Step__r",
        align: "center",
        width: "10%",
        ellipsis: true,
        sorter: (a, b) => {
          const projectPageA = a.Project_Qualification_Step__r;
          const projectPageB = b.Project_Qualification_Step__r;
          if (!projectPageA || !projectPageB) return -1;
          return projectPageA.Name.localeCompare(projectPageB.Name);
        },
        render: (text, rec) => {
          const projectQualStep = rec.Project_Qualification_Step__r;
          if (!projectQualStep) return "-";
          return (
            <Link
              to={{ pathname: `/pqs/${projectQualStep.Id}` }}
              state={{ crumbLabel: projectQualStep.Name }}
              onClick={() =>
                record({
                  id: String(projectQualStep.Id),
                  title: String(
                    text || projectQualStep.Name || projectQualStep.Id,
                  ),
                  url: `/pqs/${projectQualStep.Id}`,
                  type: "ProjectQualificationStep",
                })
              }
            >
              {text || projectQualStep.Name}
            </Link>
          );
        },
      },
      {
        title: "Type",
        dataIndex: "Project_Page_Type__c",
        key: "Project_Page_Type__c",
        width: "10%",
        sorter: (a, b) => {
          const projectPageA = a.Project_Page_Type__c;
          const projectPageB = b.Project_Page_Type__c;
          if (!projectPageA || !projectPageB) return -1;
          return projectPageA.localeCompare(projectPageB);
        },
        filters: [
          {
            text: "Project Splash Page (Preapply)",
            value: "Project Splash Page (Preapply)",
          },
          {
            text: "Project Pre-Qualification",
            value: "Project Pre-Qualification",
          },
          { text: "Project Qualifying Page", value: "Project Qualifying Page" },
          { text: "Project Active Page", value: "Project Active Page" },
          {
            text: "Default Qualification Page",
            value: "Default Qualification Page",
          },
        ],
        onFilter: (value, record) => record.Project_Page_Type__c === value,
        render: (text, rec) => text || rec.Project_Page_Type__c || "-",
      },
      {
        title: "Description",
        dataIndex: "Project_Page_Description__c",
        key: "Project_Page_Description__c",
        width: "15%",
        align: "left",
        ellipsis: true,
        sorter: (a, b) => a.Name.localeCompare(b.Name),
        render: (text, rec) => text || rec.Project_Page_Description__c || "-",
      },
      {
        title: "Active",
        dataIndex: "Active__c",
        key: "Active__c",
        width: "4%",
        render: (text, rec) => {
          return <Checkbox disabled={true} checked={rec.Active__c} />;
        },
      },
    ],
    [navigate, record],
  );

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadAll();
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="flex items-center justify-between mb-4">
        <Typography.Title level={3} style={{ margin: 0 }}>
          Project Pages
        </Typography.Title>
        <div className="flex items-center gap-3">
          <Button
            type="primary"
            onClick={() => setShowCreateModal(true)}
            icon={
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            }
          >
            Create
          </Button>
          <Button
            type="default"
            onClick={() => setShowCloneModal(true)}
            icon={<CopyOutlined />}
          >
            Clone Page
          </Button>
          <div className="relative" style={{ maxWidth: 360 }}>
            <Input.Search
              placeholder="Search pages"
              allowClear
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setSearchOpen(false)}
            />
            {searchOpen && query.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                {searchLoading ? (
                  <div className="p-3 text-sm text-gray-500">
                    <Spin size="small" /> Searching…
                  </div>
                ) : query.length < 3 ? (
                  <div className="p-3 text-sm text-gray-500">
                    Enter at least 3 characters to search
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No pages found"
                    />
                  </div>
                ) : (
                  <List
                    dataSource={searchResults}
                    size="large"
                    renderItem={(item) => (
                      <List.Item
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        onMouseDown={() => {
                          setQuery("");
                          setSearchResults([]);
                          setSearchOpen(false);
                          record({
                            id: String(item.Id),
                            title: String(item.Name || item.Id),
                            url: `/pp/${item.Id}`,
                            type: "ProjectPage",
                          });
                          navigate(`/pp/${item.Id}`);
                        }}
                      >
                        <div className="w-full">
                          <div className="text-gray-900 text-sm font-medium">
                            {item.Name}
                          </div>
                          <div className="text-xs text-gray-500">{item.Id}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clone Project Page Modal */}
      <CloneProjectPageModal
        visible={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onSuccess={() => {
          setShowCloneModal(false);
          loadAll(); // Refresh the list
        }}
      />
      <Table
        loading={loading}
        rowKey={(r) => r.Id}
        dataSource={records}
        columns={columns}
        scroll={{ y: "calc(100vh - 280px)" }}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "50", "100"],
          onShowSizeChange: (current, size) => setPageSize(size),
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      {!loading && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Alert
            type="info"
            message={
              <Typography.Text>
                <strong>NOTE :</strong>
                <> Only first 1000 records are being Displayed.</>
              </Typography.Text>
            }
            icon={<InfoCircleOutlined />}
            style={{ width: "fit-content", alignSelf: "flex-end" }}
            showIcon
          />
        </div>
      )}
      <CreateProjectPageModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
